import { buildManuscript, charCount, mergeUnique, paragraphs, summaryFromContent, visibleSnippet } from './utils.js'

export const AI_ROLES = [
  {
    id: 'architect',
    name: '青禾',
    title: '结构编辑',
    short: '结构',
    code: 'STRUCTURE',
    group: 'creative',
    initials: '禾',
    color: '#9bb39e',
    line: '先把卡片摊开，再谈句子。',
  },
  {
    id: 'reader',
    name: '阿岚',
    title: '共情读者',
    short: '共情',
    code: 'READER',
    group: 'creative',
    initials: '岚',
    color: '#d19b84',
    line: '我只关心读到这一页时，有没有屏住呼吸。',
  },
  {
    id: 'critic',
    name: '闻野',
    title: '反方编辑',
    short: '反方',
    code: 'COUNTER',
    group: 'creative',
    initials: '野',
    color: '#9aa6bd',
    line: '如果删掉这段也成立，它就不该留下。',
  },
  {
    id: 'methodologist',
    name: '顾行舟',
    title: '研究方法师',
    short: '方法',
    code: 'METHOD',
    group: 'academic',
    initials: '舟',
    color: '#8fb3b2',
    line: '先把问题变成可以被检验、也可以被推翻的东西。',
  },
  {
    id: 'reviewer',
    name: '沈砚',
    title: '文献审阅',
    short: '文献',
    code: 'LITERATURE',
    group: 'academic',
    initials: '砚',
    color: '#b7a37d',
    line: '每一条论断，都要找到它的出处、边界与反例。',
  },
]

export const CREATIVE_ROLES = AI_ROLES.filter(function (role) { return role.group === 'creative' })
export const ACADEMIC_ROLES = AI_ROLES.filter(function (role) { return role.group === 'academic' })

export const COLUMN_LABELS = {
  inspiration: '灵感',
  draft: '草稿',
  polish: '打磨',
  done: '落定',
}

const normalize = (text = '') => text.replace(/[^\u3400-\u9fffa-zA-Z0-9]/g, '').toLowerCase()

function bigrams(text) {
  const chars = Array.from(normalize(text))
  const set = new Set()
  for (let i = 0; i < chars.length - 1; i += 1) {
    set.add(chars[i] + chars[i + 1])
  }
  return set
}

export function textSimilarity(a = '', b = '') {
  const left = normalize(a)
  const right = normalize(b)
  if (!left || !right) return 0
  if (left === right) return 1
  const A = bigrams(left)
  const B = bigrams(right)
  if (!A.size || !B.size) return 0
  let overlap = 0
  A.forEach((gram) => {
    if (B.has(gram)) overlap += 1
  })
  return (2 * overlap) / (A.size + B.size)
}

function overlapParagraphs(base, incoming) {
  const existing = paragraphs(base)
  return paragraphs(incoming).filter((paragraph) => {
    if (!paragraph) return false
    return !existing.some((candidate) => textSimilarity(candidate, paragraph) > 0.34)
  })
}

function combineCardContents(base, incoming) {
  return [base.content.trim(), ...overlapParagraphs(base.content, incoming.content)]
    .filter(Boolean)
    .join('\n\n')
}

function quoteFrom(text) {
  const pieces = text
    .replace(/\n+/g, ' ')
    .split(/(?<=[。！？])/)
    .map((piece) => piece.trim())
    .filter((piece) => piece.length > 8)
  const chosen = pieces.reduce((best, piece) => (piece.length > best.length ? piece : best), pieces[0] || '')
  return chosen.slice(0, 72)
}

function linkCountFor(project, cardId) {
  return project.connections.filter((link) => link.source === cardId || link.target === cardId).length
}

function linkedCards(project, context) {
  const contextIds = new Set(context.map((card) => card.id))
  const ids = new Set()
  project.connections.forEach((link) => {
    if (contextIds.has(link.source)) ids.add(link.target)
    if (contextIds.has(link.target)) ids.add(link.source)
  })
  context.forEach((card) => ids.delete(card.id))
  return project.cards.filter((card) => ids.has(card.id))
}

export function findMergeCandidates(cards, limit = 2) {
  const pairs = []
  for (let i = 0; i < cards.length; i += 1) {
    for (let j = i + 1; j < cards.length; j += 1) {
      const a = cards[i]
      const b = cards[j]
      const sim = textSimilarity(a.content, b.content)
      const sharedTags = (a.tags || []).filter((tag) => (b.tags || []).includes(tag))
      const combinedScore = sim + (sharedTags.length ? 0.045 : 0)
      if (sim >= 0.3 || (sim >= 0.17 && sharedTags.length)) {
        pairs.push({ a, b, sim, sharedTags, score: combinedScore })
      }
    }
  }
  pairs.sort((left, right) => right.score - left.score)
  const used = new Set()
  const results = []
  pairs.forEach(({ a, b, sim, sharedTags }) => {
    if (results.length >= limit) return
    if (used.has(a.id) || used.has(b.id)) return
    used.add(a.id)
    used.add(b.id)
    const extra = overlapParagraphs(a.content, b.content)
    const mergedContent = combineCardContents(a, b)
    const shared = sharedTags.length ? '共享「' + sharedTags.join('、') + '」线索' : '在描述同一个叙事时刻'
    results.push({
      id: 'merge-' + a.id + '-' + b.id,
      baseId: a.id,
      removeId: b.id,
      pairIds: [a.id, b.id],
      titles: [a.title, b.title],
      cards: [a, b],
      title: a.title,
      content: mergedContent,
      tags: mergeUnique([...(a.tags || []), ...(b.tags || [])]),
      similarity: Math.round(sim * 100),
      sharedTags,
      reason: '两张卡片' + shared + '，文字与意象高度重合。合并后会保留两张卡片里不重复的段落。',
      noteCount: extra.length,
    })
  })
  return results
}

const PROOF_RULES = [
  { pattern: /既使/g, replace: '即使', label: '“既使”应为“即使”' },
  { pattern: /以经/g, replace: '已经', label: '“以经”应为“已经”' },
  { pattern: /的的/g, replace: '的', label: '重复的助词“的的”' },
  { pattern: /做为/g, replace: '作为', label: '“做为”应为“作为”' },
  { pattern: /帐蓬/g, replace: '帐篷', label: '“帐蓬”应为“帐篷”' },
  { pattern: /复盖/g, replace: '覆盖', label: '“复盖”应为“覆盖”' },
  { pattern: /再所难免/g, replace: '在所难免', label: '“再所难免”应为“在所难免”' },
]

const PUNCT_RULES = [
  { pattern: /([，。！？；：])\1+/g, replace: '$1', label: '重复的标点' },
  { pattern: /，([。！？])/g, replace: '$1', label: '标点搭配不当' },
  { pattern: /[ \t]+([，。！？；：])/g, replace: '$1', label: '中文标点前的多余空格' },
  { pattern: / {2,}/g, replace: ' ', label: '多余空格' },
]

export function scanProofreads(cards) {
  const issues = []
  cards.forEach((card) => {
    paragraphs(card.content).forEach((block, blockIndex) => {
      let after = block
      const reasons = []
      PROOF_RULES.forEach((rule) => {
        const next = after.replace(rule.pattern, rule.replace)
        if (next !== after) reasons.push(rule.label)
        after = next
      })
      PUNCT_RULES.forEach((rule) => {
        const next = after.replace(rule.pattern, rule.replace)
        if (next !== after) reasons.push(rule.label)
        after = next
      })
      after = after.trim()
      if (after !== block && reasons.length) {
        issues.push({
          id: 'proof-' + card.id + '-' + blockIndex,
          cardId: card.id,
          cardTitle: card.title,
          before: block,
          after,
          reason: mergeUnique(reasons).join(' · '),
          blockIndex,
        })
      }
    })
  })
  return issues
}

export function scanStructure(project, merges = []) {
  const linkedIds = new Set()
  project.connections.forEach((link) => {
    linkedIds.add(link.source)
    linkedIds.add(link.target)
  })
  const orphanCards = project.cards.filter((card) => !linkedIds.has(card.id))
  const inspirationCards = project.cards.filter((card) => card.column === 'inspiration')
  const doneCards = project.cards.filter((card) => card.column === 'done')
  const notes = []

  if (orphanCards.length) {
    notes.push({
      id: 'structure-orphan',
      tone: 'warn',
      title: '还有卡片没有接入网络',
      detail: '《' + orphanCards.map((card) => card.title).slice(0, 3).join('》《') + '》还没有和其他卡片发生关系。先为它找一条最短的线索，结构会立刻清楚一些。',
      cardIds: orphanCards.map((card) => card.id),
    })
  }
  if (inspirationCards.length >= Math.ceil(project.cards.length * 0.32)) {
    notes.push({
      id: 'structure-sparks',
      tone: 'neutral',
      title: '灵感区比手稿区更拥挤',
      detail: '有 ' + inspirationCards.length + ' 张卡片还停在“先记下来”的阶段。挑一张最让你不安的，把它推到草稿区，故事会开始往前走。',
      cardIds: inspirationCards.map((card) => card.id),
    })
  }
  if (merges.length) {
    notes.push({
      id: 'structure-merge',
      tone: 'accent',
      title: '有一组内容值得做取舍',
      detail: '不是所有重复都要合并。但在《' + merges[0].titles.join('》与《') + '》之间，至少有一段在替另一段说话。',
      cardIds: merges[0].pairIds,
    })
  }
  if (doneCards.length) {
    notes.push({
      id: 'structure-done',
      tone: 'good',
      title: '已经有可以保护下来的句子',
      detail: '《' + doneCards.map((card) => card.title).slice(0, 2).join('》《') + '》已经落定。接下来的改动不要把它们的呼吸改掉。',
      cardIds: doneCards.map((card) => card.id),
    })
  }
  return notes.slice(0, 4)
}

export function buildFinishPlan(project) {
  const merges = findMergeCandidates(project.cards)
  const proofreads = scanProofreads(project.cards)
  const structure = scanStructure(project, merges)
  return {
    merges,
    proofreads,
    structure,
    stats: {
      cards: project.cards.length,
      words: project.cards.reduce((sum, card) => sum + charCount(card.content), 0),
      merges: merges.length,
      proofreads: proofreads.length,
      links: project.connections.length,
    },
  }
}

function dedupeConnections(connections) {
  const seen = new Set()
  return connections.filter((link) => {
    const pair = [link.source, link.target].sort().join('|')
    if (seen.has(pair)) return false
    seen.add(pair)
    return true
  })
}

export function applyFinishSelections(project, plan, selectedMergeIds, selectedProofIds) {
  let cards = project.cards.map((card) => ({ ...card, tags: [...(card.tags || [])] }))
  let connections = project.connections.map((link) => ({ ...link }))
  const mergeSet = new Set(selectedMergeIds)
  const proofSet = new Set(selectedProofIds)
  const now = new Date().toISOString()

  plan.proofreads.forEach((issue) => {
    if (!proofSet.has(issue.id)) return
    const card = cards.find((candidate) => candidate.id === issue.cardId)
    if (!card) return
    if (card.content.includes(issue.before)) {
      card.content = card.content.replace(issue.before, issue.after)
      card.summary = summaryFromContent(card.content, card.summary)
      card.updatedAt = now
    }
  })

  plan.merges.forEach((merge) => {
    if (!mergeSet.has(merge.id)) return
    const base = cards.find((card) => card.id === merge.baseId)
    const other = cards.find((card) => card.id === merge.removeId)
    if (!base || !other) return
    base.title = merge.title
    base.content = combineCardContents(base, other)
    base.tags = mergeUnique([...(base.tags || []), ...(other.tags || [])])
    base.notes = [base.notes, other.notes].filter(Boolean).join('\n\n')
    base.summary = summaryFromContent(base.content, base.summary)
    base.updatedAt = now
    cards = cards.filter((card) => card.id !== other.id)
    connections = connections.map((link) => ({
      ...link,
      source: link.source === other.id ? base.id : link.source,
      target: link.target === other.id ? base.id : link.target,
    }))
    connections = connections.filter((link) => link.source !== link.target)
  })
  connections = dedupeConnections(connections)

  return {
    cards,
    connections,
    doc: buildManuscript(project, cards),
  }
}

export function generateRoundtable({ project, context, query = '', isIntro = false }) {
  const allCards = project.cards
  const selected = context && context.length ? context : allCards.slice(0, 3)
  const primary = selected[0] || allCards[0]
  if (!primary) {
    return { messages: [], synthesis: null, roundTitle: '还没有可讨论的卡片' }
  }
  const contextIds = new Set(selected.map((card) => card.id))
  const contextLinks = project.connections.filter((link) => contextIds.has(link.source) || contextIds.has(link.target))
  const primaryLinks = linkCountFor(project, primary.id)
  const related = linkedCards(project, selected)
  const colNames = mergeUnique(selected.map((card) => COLUMN_LABELS[card.column] || '灵感'))
  const totalWords = selected.reduce((sum, card) => sum + charCount(card.content), 0)
  const snippet = quoteFrom(primary.content)
  const allMerges = findMergeCandidates(allCards, 3)
  const relevantMerge = allMerges.find((merge) => merge.pairIds.some((id) => contextIds.has(id))) || allMerges[0]
  const allProofs = scanProofreads(allCards)
  const contextProofs = allProofs.filter((issue) => contextIds.has(issue.cardId))
  const doneCards = allCards.filter((card) => card.column === 'done')
  const question = (query || '').trim()

  const asksMerge = /合并|整合|重复|删减|精简|取舍/.test(question)
  const asksEnding = /结尾|收束|完成|落定|定稿|结束|最后/.test(question)
  const asksStructure = /结构|拆分|拆开|顺序|节奏|线索|场景/.test(question)
  const asksProof = /校对|错字|文字|语病|标点|润色/.test(question)
  const asksEmotion = /情感|情绪|人物|读者|成立|感觉/.test(question)
  const asksResearch = /研究|文献|证据|引用|来源|方法|样本|变量|假设|访谈|问卷|量表|编码|理论|学术|论文|数据|可证伪|综述|研究设计/.test(question)
  const asksCitation = /文献|引用|来源|综述|参考|出处/.test(question)
  const asksMethod = /方法|样本|变量|假设|访谈|问卷|量表|数据|实验|研究设计|编码/.test(question)

  const mergeLine = relevantMerge
    ? '《' + relevantMerge.titles[0] + '》与《' + relevantMerge.titles[1] + '》的文字重合度约 ' + relevantMerge.similarity + '%。' + (asksMerge ? '如果合并，我会保留“' + (paragraphs(relevantMerge.cards[0].content)[0] || relevantMerge.title).slice(0, 24) + '……”这个起手，把另一张里独有的细节接到后面。' : '这不是错，但你需要决定：它们是彼此的镜像，还是同一个场景的两份草稿？')
    : ''

  const structureParagraphs = isIntro
    ? [
        '我先按叙事功能把这 ' + selected.length + ' 张卡片摊开。它们分布在「' + colNames.join(' / ') + '」，一共 ' + totalWords + ' 字。' + (primaryLinks ? '《' + primary.title + '》牵着 ' + primaryLinks + ' 条关系，是这组材料目前的受力点。' : '《' + primary.title + '》还没有和其他卡片接上，像一块暂时离岸的石头。'),
        mergeLine || '现在最值得确认的是《' + primary.title + '》的场景任务：是谁进来，谁离开，什么发生了变化。如果一张卡同时承担三个任务，它就会在读者那里变得拥挤。',
        '我今天不会动你的句子。先把卡片的位置和关系排顺，后面的改写会轻很多。',
      ]
    : asksMerge
      ? [
          '合并是结构动作，不是文字动作。先看这组卡片里的关系：' + (mergeLine || '目前没有高度重复的文本，所以不要为了整齐而合并。'),
          '如果由我来做，我会先保留能推进时间的句子，把纯氛围的段落移进合并后的第二段。这样读者先获得信息，再停下来感受。',
          '另外，' + (contextLinks.length ? contextLinks.length + ' 条现有关系里有重复的连接，建议在合并后重新检查方向。' : '这组卡片之间还没有足够的接线，合并前先补一条因果。'),
        ]
      : asksEnding
        ? [
            '我看了落定区的 ' + doneCards.length + ' 张卡片。结尾的材料是够的，但它现在更像“解释发生了什么”，而不是“让人物再做一个选择”。',
            '《' + primary.title + '》放在这个位置很关键。如果它是最后一张，我会让它停在一个动作上，而不是一句总结。' + (mergeLine ? '同时，' + mergeLine : ''),
            '一个可执行的判断：把最后两段的顺序对调，看看哪一版更像潮水退下去之后留下的东西。',
          ]
        : asksProof
          ? [
              '我先把结构问题放到一边。文字层面目前有 ' + allProofs.length + ' 处可以确定的问题，其中 ' + contextProofs.length + ' 处就在这组卡片里。',
              contextProofs.length ? '最值得先修的是《' + contextProofs[0].cardTitle + '》里的一处表达：' + contextProofs[0].reason + '。' : '这组卡片没有明显的错字。真正需要留意的是长句的呼吸，以及同类意象的密度。',
              '修完之后再读一遍，不要在同一次修改里既动结构又动句子。你会分不清是哪一处改动让文本变好的。',
            ]
          : [
              '我把这 ' + selected.length + ' 张卡片放在一起看。它们目前落在「' + colNames.join(' / ') + '」，合计 ' + totalWords + ' 字。' + (primaryLinks ? '《' + primary.title + '》正在承担 ' + primaryLinks + ' 条关系，它更像一个枢纽。' : '《' + primary.title + '》还没有接线，我会先给它找一个最近的邻居。'),
              '对你刚才的问题，我的第一反应是：先别增加内容，先减少一个任务。' + (asksStructure ? '这张卡现在的段落数适合拆开——把场景和解释分到两张卡上，节奏会更清楚。' : '把《' + primary.title + '》里那句解释删掉，看看画面本身能不能站住。'),
              relevantMerge ? '还有一处结构机会：' + mergeLine : '如果只改一个地方，我会重排最后两张卡片的先后，让情绪先发生，再被理解。',
            ]

  const readerParagraphs = isIntro
    ? [
        '读《' + primary.title + '》时，我在这句停了一下：“' + snippet + '”',
        '它没有急着解释人物为什么回来，但那种“已经在这里站了很久”的感觉先到了。这说明你写的不是背景，是记忆。',
        '我还在等一个更私人的反应。不是环境里又多了一个隐喻，而是这个细节让她想起谁。' + (selected.length > 1 ? '其余几张卡片可以晚一点给她答案。' : ''),
      ]
    : asksEmotion
      ? [
          '我先说身体反应：读到《' + primary.title + '》的中段，我是愿意跟着走的，但还没有被击中。',
          '现在的情绪大多是环境给的。它们很统一，也很安全。真正危险的东西还没有出现：她到底希望谁看见她变成什么样的人？',
          '如果要让这一页成立，给人物一个不体面的、具体的愿望。读者会因此相信她。',
        ]
      : asksEnding
        ? [
            '结尾让我想起退潮后留在沙上的东西：形状还在，水已经走了。这个意象是成立的。',
            '但我没有感到“失去”，只感到“解释完了”。也许因为你把答案给得太完整。让人物少说一句话，或者先转身，余震会更长。',
            '保留最后那个具体的画面。它不需要承担象征，只需要出现。',
          ]
        : asksMerge
          ? [
              '如果合并这两张，我最舍不得的是那种“同一个念头出现了两次”的犹疑。它不一定低效，有时人物就是会反复回到同一个地方。',
              '所以先问：重复是作者没想清楚，还是人物没想清楚？前者可以合并，后者应该保留，甚至再让她多走一遍。',
              '《' + primary.title + '》里的“' + snippet.slice(0, 28) + '……”如果删掉，我会失去一个入口。请把它留下。',
            ]
          : asksProof
            ? [
                '文字变干净以后，情感有时反而变薄。校对时请留意，不要把那些犹豫、停顿和重复全部修平。',
                '《' + primary.title + '》里“' + snippet.slice(0, 30) + '……”的节奏很好。即使它有轻微的重复，也建议保留，因为那像人在说话。',
                '先修错字，再读一遍，不要顺手把语气也改掉。',
              ]
            : [
                '我读到《' + primary.title + '》时，最愿意相信的是“' + snippet.slice(0, 32) + '……”这里的湿度。',
                '但人物现在像一个观察者，而不是一个会被改变的人。她看见了很多东西，却没有做一个会让自己后悔的选择。',
                '给她一个动作。不是更用力地悲伤，而是做一件小事，然后马上后悔。',
              ]

  const criticParagraphs = isIntro
    ? [
        '先泼一点冷水：这组卡片里的氛围很足，但人物还常常被环境推着走。环境细节都很好，也都太安全。',
        '《' + primary.title + '》里“' + snippet + '”很漂亮。问题是，漂亮不等于必要。把这句话放回场景：谁在听？听完之后做了什么？',
        contextProofs.length ? '另外，校对层面已有 ' + contextProofs.length + ' 处小问题。别让它们留到最后，它们会伪装成风格。' : '另外，几条关系都在说同一件事。至少让一条线承担反对意见。',
      ]
    : asksMerge
      ? [
          '我反对现在就合并。你把两张卡片并排，读者才会自己去比较；一合并，那个迟疑就消失了。',
          '如果这两张真的重合到 ' + (relevantMerge ? relevantMerge.similarity + '%' : '很高') + '，那更该问：为什么你会写下两遍？是因为人物没有想清楚，还是因为你不敢让她说第二遍？',
          '给我一个不合并的理由。如果找不到，再合并。',
        ]
      : asksEnding
        ? [
            '如果你问我“结尾成不成立”，我会说：还太想被理解了。',
            '最后一句太漂亮了，也太像作者替人物总结。把它删掉，留一个不解释的空镜，也许更好。',
            '结尾不需要证明主题。它需要让前面发生过的事，在读者心里再响一次。',
          ]
        : asksProof
          ? [
              '文字问题只是表面。现在更危险的是：你把同一种情绪和环境绑得太紧，读者很快就会预测下一句。',
              '校对完以后，请再问一遍：这段里有没有一个细节是只有这个人物才会做的？如果没有，句子再干净也只是布景。',
              '还有，别把所有的“她”都修成同一个声音。',
            ]
          : [
              '这段里我最不相信的是“' + snippet.slice(0, 28) + '……”。不是因为它错，而是因为它太像一句可以被引用的话。',
              '人物在真正的时刻通常说不出这么完整的话。她会说错、停顿，或者干脆去洗碗。',
              '最后，' + (relevantMerge ? '别急着合并《' + relevantMerge.titles.join('》和《') + '》。先问它们是否在替同一件事找借口。' : '让一张卡片承担反面的证据，整本书会好看很多。'),
            ]

  const methodParagraphs = isIntro
    ? [
        '我先把这组卡片当研究材料，而不是当结论。当前最值得界定的是《' + primary.title + '》：它提供了观察，但对分析单位、成立条件和判准还没有交代。',
        '一个最小设计是四行：研究问题、分析单位、可反驳的假设、需要的数据。试着把“' + snippet.slice(0, 26) + '……”改写成“在什么条件下，什么与什么之间的关系会发生变化”的问题。',
        '下一步不要继续加材料。先建一张方法备忘卡，把名词定义和判准写清楚。否则后面的证据会各说各话。',
      ]
    : asksMethod
      ? [
          '你问到方法，我先给一个可以执行的版本。现在这组卡片能支持“探索性研究”，还不足以支持因果结论。',
          '建议先确定分析单位——是一个人、一段文本、一个事件，还是一种反复出现的场景模式。' + (selected.length > 2 ? '当前 ' + selected.length + ' 张卡片可以先视为目的性样本，而不是代表性样本。' : '先从这组卡片里提炼一个关键案例，再决定下一步补多少材料。'),
          '如果准备做访谈、问卷或编码，先别扩充问题清单。先写清楚：哪一个结果会推翻你的假设？',
        ]
      : asksResearch
        ? [
            '我会把研究拆成四步：问题、概念、证据、判准。现在这组卡片有材料，但“想研究什么”和“能观察到什么”还混在一起。',
            '把《' + primary.title + '》暂时改写成一句可证伪的命题：在什么条件下，谁或什么会发生什么变化，而什么现象可以证明它不成立。没有反例条件，研究问题就还没有落地。',
            '今天先产出一页研究备忘，不要写正文。正文会诱使你把尚不确定的判断写得像结论。',
          ]
        : asksMerge
          ? [
              '合并对研究来说通常是一次编码决定。两张卡片如果描述的是不同构念，合在一起会制造一个看起来更整齐、实际不可检验的变量。',
              '先问：这两张卡说的是同一个分析单位吗？如果是，合并并记录理由；如果不是，保留差异，用一条关系标注它们可能的关系。',
              '研究里，保留反例往往比制造一致更有价值。',
            ]
          : asksEnding
            ? [
                '从研究写作的角度看，结论不是把前面的材料再总结一遍，而是回答最初的问题，并说明证据能支持到哪一步。',
                '《' + primary.title + '》现在的强度更像“解释性结论”。把“因此”换成“这提示”，并补一句局限和替代解释。',
                '如果结论无法被数据反驳，它仍然只是观点。',
              ]
            : asksProof
              ? [
                  '校对之前先统一术语。同一个概念在卡片里换了名字，读者会以为是两个变量。',
                  '把核心概念列成一个小表：术语、定义、可观察指标、来源。校对只改表达，不改定义。',
                  '如果目前还没有定义，先补定义，再修错别字。',
                ]
              : [
                  '我把这组卡片当作现有材料。它们可以生成研究问题，但还不能单独支撑普遍结论。',
                  '建议先做一张“主张—证据—反例”表。每张卡片只保留一个主要主张，再把支持它的观察放到旁边。',
                  '下一步：把《' + primary.title + '》改写成可证伪的问题，并列出至少一条会推翻它的证据。',
                ]

  const reviewParagraphs = isIntro
    ? [
        '文献上，我不建议现在就写“已有研究表明”。这组卡片里还没有可核验的来源。',
        '《' + primary.title + '》中的“' + snippet.slice(0, 30) + '……”需要区分：它是一手材料、你的观察，还是来自某个理论概念？把三者分开，综述才不会变成感想。',
        '我不会替你编造参考文献。先列检索式——核心概念、相邻领域、方法或场景——再去数据库核对近五年的综述，并主动寻找至少一条反例。',
      ]
    : asksCitation
      ? [
          '先别生成参考文献。把《' + primary.title + '》里的每个判断标成三类：已有研究、原始材料、作者推论。',
          '检索式可以写成三组：核心概念的同义词；相邻领域的理论；反例或失败案例。每组先找一篇高质量综述，再顺着综述找原始研究。',
          '每篇文献只记四项：作者与年份、核心主张、适用范围、与这张卡的关系。凡是没核对原文的，都标为“待核验”。',
        ]
      : asksResearch
        ? [
            '从文献审阅角度，这一节缺少的不是更多引用，而是“研究缺口”的论证。',
            '先说明已有研究解释了什么、没有解释什么，再说明为什么你的卡片材料能补上这个缺口。否则引用只是装饰。',
            '把打算引用的每一篇文献都当成反方来读：它有没有可能已经解释了你的现象？',
          ]
        : asksMethod
          ? [
              '方法选择也需要文献依据。不要只写“采用访谈法”，要说明为什么访谈能回答这个问题，以及这种方法的已知局限。',
              '先找两类方法文献：同一研究问题的既有设计，以及该方法的效度与伦理讨论。',
              '如果找不到前人类似设计，这不等于创新，可能意味着问题还没有被清楚地表述。',
            ]
          : asksMerge
            ? [
                '两张卡片合并之后，引用关系会变得模糊：读者不知道某个判断来自哪一段材料。',
                '如果它们来自不同来源，先保留两张卡并建立“支持 / 反驳 / 并列”的关系；除非来源相同且分析单位一致。',
                '在文献工作里，合并相似说法很容易把差异一起删掉。',
              ]
            : asksProof
              ? [
                  '校对文字时顺便检查术语和引用的对应关系：定义是否一致，引用是否仍然指向它支持的那句话。',
                  '如果卡片改过标题或顺序，旧批注与引用可能已经错位。先核验，再统一表述。',
                  '没有出处的漂亮句子，先不要写成研究结论。',
                ]
              : [
                  '我在这组卡片里看到的是经验材料，不是文献综述。先不要假装它已经是研究现状。',
                  '为《' + primary.title + '》补一张来源卡：谁说的、在哪里说、基于什么材料、适用边界是什么。',
                  '如果暂时没有来源，就把这句话标成“个人观察”。这不是缺陷，诚实区分会让后面的检索更快。',
                ]

  const architect = {
    role: 'architect',
    paragraphs: structureParagraphs.filter(Boolean),
    note: '结构建议：先判断《' + primary.title + '》的叙事任务，再决定它的位置。' + (relevantMerge ? ' 合并候选已在收束工作室准备好。' : ''),
    bullets: [
      '当前 ' + contextLinks.length + ' 条关系穿过这组卡片。',
      relevantMerge ? '重合线索：' + relevantMerge.titles.join(' / ') + '。' : '先补一条因果，而不是再加一条氛围。',
    ],
  }
  const reader = {
    role: 'reader',
    paragraphs: readerParagraphs,
    note: '阅读反馈：让人物做一个会后悔的小动作，比继续解释环境更有力量。',
    bullets: ['情绪入口：' + primary.title, doneCards.length ? '落定区已有 ' + doneCards.length + ' 张卡，保护它们的语气。' : '目前还没有卡片落定。'],
  }
  const critic = {
    role: 'critic',
    paragraphs: criticParagraphs,
    note: '反方意见：删掉一句最漂亮的总结，看看场景还能不能站住。',
    bullets: ['试着删掉一句解释，而不是再加一句描写。', allProofs.length ? '校对清单里还有 ' + allProofs.length + ' 处待处理。' : '文字层面已经比较干净。'],
  }
  const methodologist = {
    role: 'methodologist',
    paragraphs: methodParagraphs,
    note: '研究动作：把《' + primary.title + '》改写成一句可证伪的研究问题，并列出至少一条会推翻它的证据。',
    bullets: [
      '分析单位待明确：人物、文本、事件，还是场景模式？',
      '下一份产出：研究问题卡 + 「主张—证据—反例」表。',
    ],
  }
  const reviewer = {
    role: 'reviewer',
    paragraphs: reviewParagraphs,
    note: '文献动作：先建一张来源卡，记录作者、年份、核心主张、适用范围与反例；未核验的引用保持“待核验”。',
    bullets: [
      '引用原则：不生成未核验的文献，不把个人观察写成“研究表明”。',
      '检索提示：核心概念 × 相邻领域 × 方法或场景，再补一类反例。',
    ],
  }

  let consensus = '这组卡片分布在「' + colNames.join(' / ') + '」，情绪方向是一致的；真正需要决定的是谁承担推进，谁负责停顿。'
  let tension = relevantMerge ? '青禾主张先合并重复线索，闻野担心合并后失去那种反复回到同一处的犹疑。' : '青禾想先定结构任务，阿岚坚持先看见人物的私人欲望。'
  let next = '下一轮之前，只做一件小事：把《' + primary.title + '》的最后一句改成一个可见的动作。'
  let research = '把《' + primary.title + '》改写成一句可证伪的问题，再为它列出至少一条反例。'
  if (asksEnding) {
    consensus = '创作编辑一致认为：结尾的材料已经足够，缺的是让读者自己完成最后一步的空间。'
    tension = '青禾建议对调最后两段，闻野则主张直接删掉那句点题的句子；顾行舟提醒，结论不能超出证据。'
    next = '删掉最后一句解释性的总结，再读一遍结尾。'
  }
  if (asksMerge) {
    consensus = '合并可以降低信息重复，但这组卡片的价值也许正在于“同一件事被说了两遍”。'
    tension = relevantMerge ? '结构上该合并，情感上未必。先判断重复来自作者还是人物；顾行舟会把这看作一次编码决定。' : '创作侧不建议为了整齐而合并；沈砚提醒，先确认两张卡是否来自同一来源。'
    next = '打开收束工作室，先看合并预览，再做决定。'
  }
  if (asksProof) {
    consensus = '文字层面有明确可修的 ' + allProofs.length + ' 处，但不要在校对时顺手改变叙述语气。'
    tension = '青禾想一起修结构，闻野反对在同一次修改里同时动结构和句子。'
    next = '先应用校对清单，再回到圆桌讨论结构。'
  }
  if (asksStructure) {
    consensus = '这一组卡片的受力点不在句子，而在每张卡承担了太多任务。'
    tension = '阿岚担心拆分后失去连贯的气息，青禾认为拆开才会露出真正的转折。'
    next = '把《' + primary.title + '》按“场景 / 解释 / 回忆”拆成最多三张卡，再决定哪一张留下。'
  }
  if (asksEmotion) {
    consensus = '氛围已经建立，但人物的私人欲望还没有被读者看见。'
    tension = '闻野认为现在的含蓄是逃避，阿岚认为再多说一句就会破坏味道。'
    next = '给人物一个不体面的具体愿望，写在卡片背面，不必马上放进正文。'
  }
  if (asksCitation) {
    research = '为《' + primary.title + '》建立一张来源卡：作者与年份、核心主张、适用范围、反例；没有核对原文的文献标注“待核验”。'
  }
  if (asksMethod) {
    research = '写一页研究备忘：研究问题、分析单位、可证伪假设、数据结构。先不要扩充正文。'
  }
  if (asksResearch) {
    consensus = '学术侧先给出了研究支架：问题、概念、证据、反例。创作侧的材料可以保留，但不能直接当作结论。'
    tension = '顾行舟要求先缩小研究问题，沈砚坚持先补来源与边界；创作侧担心过早框架会压扁材料。'
    next = '先写一页研究备忘，不进入正文。'
    research = '在《' + primary.title + '》旁建一张研究卡：研究问题、分析单位、可证伪假设、需要的数据与来源类型。'
  }

  return {
    messages: [architect, reader, critic, methodologist, reviewer],
    synthesis: { consensus, tension, next, research },
    roundTitle: isIntro ? '先一起读一遍' : '关于“' + (question.length > 20 ? question.slice(0, 20) + '…' : question) + '”',
  }
}
